/**
 * LaunchPanel — the launch stage of the campaign canvas.
 *
 * One in-canvas panel replaces the three publish modals: segmented
 * paid/organic, organic concept selection, timing, an exact-spend summary,
 * and a two-step confirm (first click arms, second commits). Confirming
 * records the launch approval with a snapshot of this configuration.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { cn } from '@/lib/utils';
import { TIME_SLOTS, toSlot } from '@/lib/publishSlots';
import { useAuth } from '@/hooks/useAuth';
import { apiClient } from '@/services/api';
import { brandKnowledgeApi, PlanCampaignRef } from '@/services/brandKnowledgeApi';
import { AdvancedStrategyPanel } from '../chat/AdvancedStrategyPanel';
import { advancedConfigFor } from './StrategyPanel';
import {
  createPinterestBoard,
  loadFacebookPages,
  loadPinterestBoards,
  loadPublishAccounts,
  type PageOption,
} from './publishService';
import type { CampaignState, Gate, LaunchAccount, Stage } from './types';

interface TrackingResult {
  platform: string;
  status: 'ok' | 'warn' | 'block' | 'unknown';
  checks: { key: string; ok: boolean; text: string; severity: 'warn' | 'block' | null }[];
}

/** When a monthly plan's campaign goes live: its start date at 09:00, or as soon as it's confirmed
 *  when that date is today or past; and how many days it runs, to its end date. The same rule as
 *  the server's production.review.launch_timing, which sets it when the ads are approved. */
export const planTiming = (ref: Pick<PlanCampaignRef, 'start_date' | 'end_date'>, now = new Date()) => {
  const pad = (n: number) => String(n).padStart(2, '0');
  const today = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
  const start = ref.start_date.slice(0, 10), end = ref.end_date.slice(0, 10);
  const first = start > today ? start : today;
  const days = Math.max(1, Math.round((Date.parse(end) - Date.parse(first)) / 86400000) + 1);
  return start > today
    ? { type: 'scheduled' as const, at: `${start}T09:00`, days, start, end }
    : { type: 'now' as const, at: null, days, start, end };
};

const dayText = (iso: string) => new Date(`${iso.slice(0, 10)}T00:00`).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });

export interface LaunchPanelProps {
  state: CampaignState;
  readonly: boolean;
  onEdit: (path: string, value: unknown, label?: string) => Promise<unknown>;
  /** Resolves with the failure message when the launch did not go through. */
  onApprove: (gate: Gate, launchConfig?: Record<string, unknown>) => Promise<string | void> | void;
  approving: boolean;
  conversationId?: string;
  stage?: Stage;
  isDemo?: boolean;
  /** Ask the campaign chat for a change (opens the chat and sends it there). */
  onAskChat?: (text: string) => void;
}

export const LaunchPanel: React.FC<LaunchPanelProps> = ({
  state,
  readonly,
  onEdit,
  onApprove,
  approving,
  conversationId,
  stage,
  isDemo = false,
  onAskChat,
}) => {
  const isLive = stage === 'live';
  const [armed, setArmed] = useState(false);
  const [isLaunching, setIsLaunching] = useState(false);
  const [launchError, setLaunchError] = useState<string | null>(null);
  const { currentOrganization } = useAuth();

  const launch = state.launch ?? {};
  const mode = launch.mode ?? 'paid';
  const timingType = launch.timing?.type ?? 'now';
  const scheduledAt = launch.timing?.at ?? null;

  // A campaign from the monthly plan launches on the plan's dates: the owner reviews and confirms,
  // and it is scheduled as planned. They can still pick another date. Campaigns made in chat keep
  // the Now / Scheduled choice.
  const [planRef, setPlanRef] = useState<PlanCampaignRef | null>(null);
  useEffect(() => {
    if (!conversationId || isDemo) return;
    let off = false;
    brandKnowledgeApi.planCampaigns()
      .then(list => { if (!off) setPlanRef(list.find(x => x.campaign_id === conversationId) || null); })
      .catch(() => undefined);
    return () => { off = true; };
  }, [conversationId, isDemo]);
  const planned = planRef ? planTiming(planRef) : null;
  const ownDate = launch.timing?.source === 'owner';
  const followsPlan = !!planned && !ownDate && !isLive;
  // What confirming does: the plan's timing for a plan campaign, else the panel's own choice.
  const whenType = followsPlan && planned ? planned.type : timingType;
  const whenAt = followsPlan && planned ? planned.at : scheduledAt;
  const planApplied = useRef(false);
  useEffect(() => {
    // The plan's timing goes into the Launch step (once per visit) when it isn't there yet: the ads
    // were approved in the campaign itself rather than on its review page, or the dates moved.
    if (!planned || !followsPlan || readonly || planApplied.current) return;
    planApplied.current = true;
    if (timingType !== planned.type) onEdit('launch.timing.type', planned.type, planned.type === 'scheduled' ? 'Launch timing: scheduled, from your plan' : 'Launch timing: now');
    if (planned.at && scheduledAt !== planned.at) onEdit('launch.timing.at', planned.at, 'Scheduled launch time from your plan');
    if (Number(state.budget?.durationDays) !== planned.days) onEdit('budget.durationDays', planned.days, 'Runs to your plan’s end date');
  }, [planned, followsPlan, readonly, timingType, scheduledAt, state.budget?.durationDays, onEdit]);
  const budget = state.budget ?? {};
  const currency = budget.currency === 'INR' ? '₹' : budget.currency === 'EUR' ? '€' : budget.currency === 'GBP' ? '£' : '$';

  // Per-platform budget health — fetched once when entering paid mode to gate launch.
  const [budgetHealthMap, setBudgetHealthMap] = useState<Record<string, { status: string; implications: string }>>({});

  // Tracking check — is what each platform optimises for actually being counted (pixel, event,
  // conversion actions)? A campaign that would count nothing needs an explicit "launch anyway".
  const [trackingMap, setTrackingMap] = useState<Record<string, TrackingResult>>({});
  const [ackTracking, setAckTracking] = useState(false);

  const fetchBudgetHealth = useCallback(async () => {
    if (mode !== 'paid' || isDemo) return;
    const platforms = Object.keys(state.budget?.split ?? {});
    const targets = platforms.length ? platforms : ['meta'];
    const results: Record<string, { status: string; implications: string }> = {};
    const tracking: Record<string, TrackingResult> = {};
    await Promise.all(
      targets.map(async (platform) => {
        let adSet: { optimization_goal?: string; promoted_object?: { pixel_id?: string; custom_event_type?: string } } | undefined;
        try {
          const resp = await apiClient.get(
            `/api/v1/campaigns/${conversationId}/compiled-preview?platform=${platform}`,
          );
          const bh = resp.data?.budget_health;
          if (bh?.status) results[platform] = { status: bh.status, implications: bh.implications ?? '' };
          adSet = resp.data?.ad_sets?.[0];
        } catch {
          // non-critical — don't block launch if health fetch fails
        }
        try {
          const key = platform === 'google' ? 'google_ads' : platform;
          const params: Record<string, string> = {};
          if (key === 'meta') {
            if (adSet?.optimization_goal === 'LEAD_GENERATION') params.lead_form = 'true';
            if (adSet?.promoted_object?.pixel_id) params.pixel_id = String(adSet.promoted_object.pixel_id);
            if (adSet?.promoted_object?.custom_event_type) params.event = String(adSet.promoted_object.custom_event_type);
            if (!adSet || !['OFFSITE_CONVERSIONS', 'VALUE', 'LEAD_GENERATION'].includes(adSet.optimization_goal || '')) return;
          } else if (key !== 'google_ads') return;
          const t = (await apiClient.get(`/api/v1/publisher/${key}/tracking-check`, { params })).data as TrackingResult;
          if (t?.status && t.status !== 'unknown') tracking[platform] = t;
        } catch {
          // non-critical
        }
      }),
    );
    setBudgetHealthMap(results);
    setTrackingMap(tracking);
  }, [mode, isDemo, conversationId, state.budget?.split]);

  useEffect(() => {
    fetchBudgetHealth();
  }, [fetchBudgetHealth]);

  // Separate published-key sets per publish type so organic posts don't lock
  // paid-ad accounts and vice versa.
  const [paidPublishedKeys, setPaidPublishedKeys] = useState<Set<string>>(new Set());
  const [organicPublishedKeys, setOrganicPublishedKeys] = useState<Set<string>>(new Set());
  useEffect(() => {
    if (!conversationId) return;
    apiClient
      .get(`/api/v1/conversations/${conversationId}/platform-assignments`)
      .then((resp) => {
        const assignments: any[] = resp.data?.assignments ?? [];
        const key = (a: any) => `${a.platform}:${a.platform_account_id}`;
        setPaidPublishedKeys(new Set(
          assignments
            .filter((a) => a.status === 'published' && (!a.publish_type || a.publish_type === 'paid_ad'))
            .map(key),
        ));
        setOrganicPublishedKeys(new Set(
          assignments
            .filter((a) => a.status === 'published' && a.publish_type === 'organic_post')
            .map(key),
        ));
      })
      .catch(() => {});
  }, [conversationId]);
  const criticalPlatforms = Object.entries(budgetHealthMap)
    .filter(([, bh]) => bh.status === 'critical')
    .map(([p]) => p);
  const hasCriticalBudget = mode === 'paid' && criticalPlatforms.length > 0;

  // Connected ad accounts (OAuth connections) for the campaign's platforms.
  // Loaded once, then reconciled with any enabled-flags already persisted in
  // launch.accounts so the selection survives reloads via history replay.
  const [accounts, setAccounts] = useState<LaunchAccount[] | null>(null);
  const [accountsError, setAccountsError] = useState<string | null>(null);
  const persistedAccounts = launch.accounts;

  useEffect(() => {
    // Demo campaigns have no connected OAuth accounts. Inject a synthetic
    // "Demo Ad Account" so the publish button is reachable; the actual publish
    // is short-circuited to a dummy result in handleApproveGate (no publisher
    // call, no real ads). A paid Meta account needs no page/board sub-target.
    if (isDemo) {
      setAccounts([{
        platform: 'meta',
        accountId: 'demo-ad-account',
        accountName: 'Demo Ad Account',
        connectionId: 'demo-connection',
        enabled: true,
        isDefault: true,
      }]);
      return;
    }
    let cancelled = false;
    const platforms = Object.keys(state.budget?.split ?? {});
    // Fall back to every platform we can publish to, so a campaign whose
    // budget split hasn't been set yet still surfaces its connected accounts.
    loadPublishAccounts(
      platforms.length ? platforms : ['meta', 'google', 'pinterest', 'twitter'],
      currentOrganization?.id,
    )
      .then((loaded) => {
        if (cancelled) return;
        const merged = loaded.map((a) => {
          const persisted = persistedAccounts?.find(
            (p) => p.connectionId === a.connectionId,
          );
          if (!persisted) return a;
          return {
            ...a,
            enabled: persisted.enabled !== false,
            // Restore user's page/board selection so auto-pick doesn't revert to default on reload.
            ...(persisted.pageId && { pageId: persisted.pageId, pageName: persisted.pageName }),
            ...(persisted.boardId && { boardId: persisted.boardId }),
          };
        });
        setAccounts(merged);
      })
      .catch((e) => {
        if (!cancelled) setAccountsError(e?.message || 'Could not load connected accounts');
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentOrganization?.id, isDemo]);

  const enabledAccounts = (accounts ?? []).filter(
    (a) => a.enabled !== false && !(mode === 'paid' && paidPublishedKeys.has(`${a.platform}:${a.accountId}`)),
  );

  const toggleAccount = (connectionId: string) =>
    disarmAnd(() => {
      const next = (accounts ?? []).map((a) =>
        a.connectionId === connectionId ? { ...a, enabled: a.enabled === false } : a,
      );
      setAccounts(next);
      onEdit('launch.accounts', next, 'Updated launch accounts');
    });

  // Sub-targets: Meta organic posts need a Facebook page; Pinterest pins
  // need a board. Options load lazily per enabled connection; the backend's
  // default is auto-picked so most users never touch the select.
  const [subTargets, setSubTargets] = useState<Record<string, PageOption[]>>({});

  const needsPage = (a: LaunchAccount) => a.platform === 'meta';
  const needsBoard = (a: LaunchAccount) => a.platform === 'pinterest';

  useEffect(() => {
    if (!accounts) return;
    let cancelled = false;
    accounts
      .filter((a) => a.enabled !== false && (needsPage(a) || needsBoard(a)))
      .filter((a) => subTargets[a.connectionId!] === undefined)
      .forEach(async (a) => {
        const options = needsBoard(a)
          ? await loadPinterestBoards(a.connectionId!)
          : await loadFacebookPages(a.connectionId!);
        if (cancelled) return;
        setSubTargets((prev) => ({ ...prev, [a.connectionId!]: options }));
        const chosen = options.find((o) => o.isDefault) ?? options[0];
        if (chosen) {
          setAccounts((prev) =>
            (prev ?? []).map((acc) =>
              acc.connectionId === a.connectionId &&
              !(needsBoard(a) ? acc.boardId : acc.pageId)
                ? needsBoard(a)
                  ? { ...acc, boardId: chosen.id }
                  : { ...acc, pageId: chosen.id, pageName: chosen.name }
                : acc,
            ),
          );
        }
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accounts, mode]);

  const setSubTarget = (connectionId: string, optionId: string) =>
    disarmAnd(() => {
      const options = subTargets[connectionId] ?? [];
      const chosen = options.find((o) => o.id === optionId);
      const next = (accounts ?? []).map((a) => {
        if (a.connectionId !== connectionId) return a;
        return a.platform === 'pinterest'
          ? { ...a, boardId: optionId }
          : { ...a, pageId: optionId, pageName: chosen?.name };
      });
      setAccounts(next);
      onEdit('launch.accounts', next, 'Updated launch destination');
    });

  // Inline Pinterest board creation (parity with the old modal's mini-form).
  const [newBoardName, setNewBoardName] = useState<Record<string, string>>({});
  const [creatingBoard, setCreatingBoard] = useState<Record<string, boolean>>({});
  const [boardError, setBoardError] = useState<Record<string, string | null>>({});

  const handleCreateBoard = async (connectionId: string) => {
    const name = (newBoardName[connectionId] ?? '').trim();
    if (!name || creatingBoard[connectionId]) return;
    setCreatingBoard((prev) => ({ ...prev, [connectionId]: true }));
    setBoardError((prev) => ({ ...prev, [connectionId]: null }));
    try {
      const board = await createPinterestBoard(connectionId, name);
      setSubTargets((prev) => ({
        ...prev,
        [connectionId]: [...(prev[connectionId] ?? []), board],
      }));
      setNewBoardName((prev) => ({ ...prev, [connectionId]: '' }));
      setSubTarget(connectionId, board.id);
    } catch (e: any) {
      setBoardError((prev) => ({ ...prev, [connectionId]: e?.message || 'Failed to create board' }));
    } finally {
      setCreatingBoard((prev) => ({ ...prev, [connectionId]: false }));
    }
  };

  /** Enabled accounts whose required page/board is still missing. */
  const missingSubTargets = enabledAccounts.filter(
    (a) => (needsPage(a) && !a.pageId) || (needsBoard(a) && !a.boardId),
  );

  const concepts = (state.creativeSet ?? []).filter((c) => c.included !== false);
  const organicSelection = launch.organicSelection ?? [];

  // When re-publishing from live stage: history (what was posted before) and
  // intent (what to post now) are separate. localSelection starts empty so the
  // user builds a fresh pick without having to deselect previous choices.
  const [localSelection, setLocalSelection] = useState<string[]>([]);
  const postedConceptIds = isLive ? new Set(organicSelection) : new Set<string>();
  const activeSelection = isLive ? localSelection : organicSelection;

  const selectedPosts = concepts.filter((c) => activeSelection.includes(c.id));

  const metaCount = concepts.reduce(
    (sum, c) => sum + (c.renditions ?? []).filter((r) => r.platform === 'meta' && r.enabled !== false).length,
    0,
  );
  const googleCount = concepts.reduce(
    (sum, c) =>
      sum +
      (c.renditions ?? []).filter(
        (r) => (r.platform === 'google' || r.platform === 'google_ads') && r.enabled !== false,
      ).length,
    0,
  );

  const disarmAnd = (fn: () => void) => {
    setArmed(false);
    fn();
  };

  const setMode = (m: 'paid' | 'organic') =>
    disarmAnd(() => onEdit('launch.mode', m, `Distribution set to ${m}`));

  const setTiming = (t: 'now' | 'scheduled') =>
    disarmAnd(() => onEdit('launch.timing.type', t, t === 'now' ? 'Launch timing: now' : 'Launch timing: scheduled'));

  const toggleOrganic = (conceptId: string) => {
    if (isLive) {
      disarmAnd(() =>
        setLocalSelection((prev) =>
          prev.includes(conceptId) ? prev.filter((id) => id !== conceptId) : [...prev, conceptId],
        ),
      );
    } else {
      disarmAnd(() => {
        const next = organicSelection.includes(conceptId)
          ? organicSelection.filter((id) => id !== conceptId)
          : [...organicSelection, conceptId];
        onEdit('launch.organicSelection', next, 'Updated organic post selection');
      });
    }
  };

  const spendText =
    mode === 'paid'
      ? `${currency}${Number(budget.total ?? 0).toLocaleString()}${budget.durationDays ? ` over ${budget.durationDays} days` : ''}`
      : 'nothing — organic posts are free';

  const whatText =
    mode === 'paid'
      ? `the full approved set (${metaCount} Meta creatives, ${googleCount} Google assets) as rotating ad variations`
      : `${selectedPosts.length} selected creative${selectedPosts.length === 1 ? '' : 's'} as page posts`;

  const trackingLines = Object.entries(trackingMap);
  const trackingBlocked = mode === 'paid' && trackingLines.some(([, t]) => t.status === 'block');
  const confirmDisabled =
    approving ||
    isLaunching ||
    enabledAccounts.length === 0 ||
    missingSubTargets.length > 0 ||
    (mode === 'organic' && selectedPosts.length < 1) ||
    (whenType === 'scheduled' && !whenAt) ||
    (trackingBlocked && !ackTracking);

  const handleLaunch = async () => {
    if (!armed) {
      setArmed(true);
      return;
    }
    setIsLaunching(true);
    setLaunchError(null);
    try {
      const failed = await onApprove('launch', {
        mode,
        timing: { type: whenType, at: whenType === 'scheduled' ? whenAt : null },
        organicSelection: mode === 'organic' ? activeSelection : [],
        accounts: enabledAccounts,
        budgetHealthSnapshot: Object.keys(budgetHealthMap).length > 0 ? budgetHealthMap : undefined,
      });
      if (typeof failed === 'string') setLaunchError(failed);
      else if (mode === 'paid' && conversationId && !isDemo) {
        // A campaign from the monthly plan: its brief records what was launched (budget, dates).
        const at = whenType === 'scheduled' ? whenAt : null;
        const start = at ? String(at).slice(0, 10) : new Date().toISOString().slice(0, 10);
        brandKnowledgeApi.syncBriefFromCampaign(conversationId, { budget: Number(budget.total) || undefined, start_date: start, days: Number(budget.durationDays) || undefined }).catch(() => undefined);
      }
      // Refetch assignments so published-key sets update immediately after publish,
      // locking/badging the accounts that were just published without a page reload.
      if (conversationId) {
        const resp = await apiClient.get(
          `/api/v1/conversations/${conversationId}/platform-assignments`,
        );
        const assignments: any[] = resp.data?.assignments ?? [];
        const key = (a: any) => `${a.platform}:${a.platform_account_id}`;
        setPaidPublishedKeys(new Set(
          assignments
            .filter((a) => a.status === 'published' && (!a.publish_type || a.publish_type === 'paid_ad'))
            .map(key),
        ));
        setOrganicPublishedKeys(new Set(
          assignments
            .filter((a) => a.status === 'published' && a.publish_type === 'organic_post')
            .map(key),
        ));
        setLocalSelection([]);
      }
    } catch {
      // errors already surfaced by handleApproveGate via canvas.error / setError
    } finally {
      setIsLaunching(false);
      setArmed(false);
    }
  };

  const row = 'grid gap-3 border-b border-line py-[18px] last:border-b-0 sm:grid-cols-[180px_minmax(0,1fr)] sm:gap-5';
  const rowTitle = 'font-display text-base font-bold text-ink';
  const days = budget.durationDays;
  const perDay = mode === 'paid' && budget.total && days ? Number(budget.total) / days : null;
  const amount = `${currency}${Number(budget.total ?? 0).toLocaleString('en-IN')}`;
  const whenText = whenType === 'scheduled' && whenAt
    ? new Date(whenAt).toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit' })
    : 'as soon as you confirm';
  const healthLines = Object.entries(budgetHealthMap);

  // What will be published, in one line: goal, spend, where, who (from the campaign's strategy).
  const intent = (state as unknown as { strategy_intent?: { campaigns?: { ad_sets?: { audience_intent?: { geo?: { centers?: string[] }; age_intent?: string; gender_intent?: string } }[] }[] } }).strategy_intent;
  const aud = intent?.campaigns?.[0]?.ad_sets?.[0]?.audience_intent;
  const summary = [
    state.objective ? String(state.objective).replace(/^OUTCOME_/i, '').toLowerCase().replace(/_/g, ' ') : null,
    perDay ? `${currency}${Math.round(perDay).toLocaleString('en-IN')}/day for ${days} days` : null,
    aud?.geo?.centers?.length ? aud.geo.centers.slice(0, 3).join(', ') + (aud.geo.centers.length > 3 ? '…' : '') : null,
    [aud?.age_intent, aud?.gender_intent].filter(x => x && x !== 'all').join(', ') || null,
  ].filter(Boolean).map(x => String(x).replace(/_/g, ' ')).join(' · ');
  const singlePlatform = Object.keys(budget.split ?? {}).length <= 1;
  const applyAdvanced = intent ? async (next: Record<string, unknown>, daily?: number) => {
    await onEdit('strategy_intent', { ...(intent as Record<string, unknown>), ...next }, 'Changed in the advanced settings');
    if (daily && singlePlatform && budget.durationDays) {
      const total = Math.round(daily * Number(budget.durationDays));
      if (total !== Number(budget.total)) await onEdit('budget.total', total, 'Daily budget changed in the advanced settings');
    }
  } : undefined;
  const [ask, setAsk] = useState('');

  return (
    <div className="flex flex-wrap items-start gap-7">
      {mode === 'paid' && !isLive && (
        <section aria-label="What will be published" className="flex basis-full flex-col gap-3 rounded-panel bg-card px-6 py-5 md:px-7">
          <div className="flex flex-wrap items-baseline justify-between gap-2">
            <div className={rowTitle}>What will be published</div>
            {summary && <div className="text-small text-ink-2">{summary}</div>}
          </div>
          <AdvancedStrategyPanel campaignConfig={advancedConfigFor(state)} onApply={readonly ? undefined : applyAdvanced} />
          {!readonly && onAskChat && (
            <form className="flex flex-wrap gap-2" onSubmit={(e) => { e.preventDefault(); if (ask.trim()) { onAskChat(ask.trim()); setAsk(''); } }}>
              <label htmlFor="launch-ask" className="sr-only">Change something</label>
              <input id="launch-ask" value={ask} onChange={(e) => setAsk(e.target.value)} maxLength={500}
                placeholder="Change something — e.g. “Only women 25–45 in Pune” or “Spend less on weekends”"
                className="min-h-[44px] min-w-0 flex-[1_1_280px] rounded-btn border border-line bg-card px-3.5 text-sm text-ink placeholder:text-ink-3 focus:border-ink focus:outline-none" />
              <button type="submit" disabled={!ask.trim()}
                className="min-h-[44px] rounded-btn bg-navy px-4 text-sm font-semibold text-on-navy disabled:opacity-50">Ask</button>
            </form>
          )}
          {!readonly && <p className="m-0 text-xs text-ink-3">Changes save to this campaign before you launch: edit the settings above, or ask in the chat and the campaign updates.</p>}
        </section>
      )}
      {/* Launch settings (C2) */}
      <section aria-label="Launch settings" className="min-w-0 flex-[999_1_520px] rounded-panel bg-card px-6 pb-2 pt-1.5 md:px-7">
        <div className={row}>
          <div className="flex flex-col gap-1"><div className={rowTitle}>Type</div></div>
          <div className="flex flex-col gap-2">
            <div role="group" aria-label="Type" className="inline-flex w-max gap-1 rounded-btn bg-ground p-1">
              {(['paid', 'organic'] as const).map((m) => (
                <button key={m} type="button" disabled={readonly} aria-pressed={mode === m} onClick={() => setMode(m)}
                  className={cn('min-h-[40px] rounded-[10px] px-4 text-sm font-semibold transition-colors', mode === m ? 'bg-navy text-on-navy' : 'text-ink hover:bg-card')}>
                  {m === 'paid' ? 'Paid ads' : 'Organic posts'}
                </button>
              ))}
            </div>
            <p className="m-0 text-small text-ink-2">
              {mode === 'paid' ? 'Publishes the whole creative set; the platforms rotate the variations automatically.' : 'Hand-pick which creatives are posted to each page.'}
            </p>
          </div>
        </div>

        {mode === 'organic' && (
          <div className={row}>
            <div className="flex flex-col gap-1"><div className={rowTitle}>What to post</div><div className="text-small text-ink-2">Pick one or more</div></div>
            <div className="flex flex-col">
              {concepts.length === 0 && <p className="m-0 text-small text-ink-3">No ideas in the campaign — go back to Creative.</p>}
              {concepts.map((c) => (
                <label key={c.id} className="flex cursor-pointer items-center gap-3 border-b border-line py-2.5 text-sm text-ink last:border-b-0">
                  <input type="checkbox" disabled={readonly} checked={activeSelection.includes(c.id)} onChange={() => toggleOrganic(c.id)} className="h-[18px] w-[18px] accent-[rgb(var(--c-ink-1))]" />
                  <span className="flex-1">{c.concept ?? c.id}</span>
                  {postedConceptIds.has(c.id) && <span className="rounded-full bg-ok-soft px-2.5 py-0.5 text-xs font-semibold text-ok">✓ Posted</span>}
                </label>
              ))}
            </div>
          </div>
        )}

        <div className={row}>
          <div className="flex flex-col gap-1"><div className={rowTitle}>Where it runs</div><div className="text-small text-ink-2">Your connected accounts</div></div>
          <div className="flex flex-col gap-2.5">
            {accountsError ? <p className="m-0 text-small text-bad">{accountsError}</p>
              : accounts === null ? <p className="m-0 text-small text-ink-3">Loading connected accounts…</p>
              : accounts.length === 0 ? (
                <div className="flex flex-wrap items-center justify-between gap-3 rounded-[14px] bg-bad-soft px-4 py-3.5 text-sm text-ink">
                  No connected ad accounts for this campaign’s platforms yet.
                  <a href="/settings?tab=connections" className="inline-flex min-h-[36px] items-center rounded-btn bg-navy px-3 font-display text-sm font-bold text-on-navy no-underline">Connect accounts</a>
                </div>
              ) : accounts.map((a) => {
                const publishedKey = `${a.platform}:${a.accountId}`;
                // Paid ads lock once published (same campaign can't run twice on one account); organic can repost.
                const locked = mode === 'paid' && paidPublishedKeys.has(publishedKey);
                const alreadyPostedOrganic = organicPublishedKeys.has(publishedKey);
                const wantsSubTarget = a.enabled !== false && !locked && (needsPage(a) || needsBoard(a));
                const options = subTargets[a.connectionId!];
                const currentTarget = needsBoard(a) ? a.boardId : a.pageId;
                const on = !locked && a.enabled !== false;
                return (
                  <div key={a.connectionId} className="flex flex-col gap-2">
                    <label className={cn('flex min-h-[52px] items-center gap-3 rounded-btn px-4 text-[15px] font-semibold transition-shadow',
                      on ? 'bg-card shadow-[inset_0_0_0_2px_rgb(var(--c-ink-1))]' : 'bg-ground', locked ? 'cursor-default text-ink-3' : !readonly && 'cursor-pointer')}>
                      <input type="checkbox" disabled={readonly || locked} checked={on} onChange={() => !locked && toggleAccount(a.connectionId!)} className="h-[18px] w-[18px] accent-[rgb(var(--c-ink-1))]" />
                      <span className="min-w-0 flex-1 truncate">
                        {a.accountName ?? a.platform}
                        {a.accountId && <span className="ml-1.5 font-money text-xs font-normal text-ink-3">{a.accountId}</span>}
                      </span>
                      {locked ? <span className="rounded-full bg-inset px-2.5 py-0.5 text-xs font-semibold text-ink-2">Already published</span>
                        : alreadyPostedOrganic ? <span className="rounded-full bg-ok-soft px-2.5 py-0.5 text-xs font-semibold text-ok">Posted</span>
                        : <span className="rounded-full bg-ok-soft px-2.5 py-0.5 text-xs font-semibold text-ok">✓ connected</span>}
                    </label>
                    {wantsSubTarget && (mode === 'organic' || needsBoard(a) || !options || options.length > 1) && (
                      <div className="flex flex-col gap-1.5 pl-1">
                        <span className="text-small text-ink-2">{needsBoard(a) ? 'Pinterest board' : 'Facebook Page'}</span>
                        {options === undefined ? <span className="text-small text-ink-3">Loading…</span>
                          : options.length === 0 ? <span className={cn('text-small', needsBoard(a) ? 'text-ink-3' : 'font-semibold text-bad')}>{needsBoard(a) ? 'No boards on this account yet — create one below.' : 'No pages found on this account — a Facebook Page is required.'}</span>
                          : (
                            <select disabled={readonly} value={currentTarget ?? ''} onChange={(e) => setSubTarget(a.connectionId!, e.target.value)}
                              className={cn('min-h-[52px] rounded-btn border-0 bg-ground px-4 text-[15px] font-semibold text-ink', !currentTarget && 'shadow-[inset_0_0_0_2px_rgb(var(--c-bad))]')}>
                              {options.map((o) => <option key={o.id} value={o.id}>{o.name}{o.isDefault ? ' (default)' : ''}</option>)}
                            </select>
                          )}
                        {needsBoard(a) && options !== undefined && !readonly && (
                          <div className="flex flex-wrap items-center gap-2">
                            <input type="text" value={newBoardName[a.connectionId!] ?? ''} maxLength={180} placeholder="New board name…"
                              onChange={(e) => setNewBoardName((prev) => ({ ...prev, [a.connectionId!]: e.target.value }))}
                              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleCreateBoard(a.connectionId!); } }}
                              className="min-h-[44px] w-56 rounded-btn border-0 bg-card px-3.5 text-sm shadow-ring-line focus:outline-none focus:shadow-ring-ink" />
                            <button type="button" disabled={creatingBoard[a.connectionId!] || !(newBoardName[a.connectionId!] ?? '').trim()} onClick={() => handleCreateBoard(a.connectionId!)}
                              className="inline-flex min-h-[44px] items-center rounded-btn px-3.5 font-display text-sm font-bold text-ink shadow-ring-line hover:shadow-ring-ink disabled:opacity-50">
                              {creatingBoard[a.connectionId!] ? 'Creating…' : '+ Create board'}
                            </button>
                            {boardError[a.connectionId!] && <span className="text-small text-bad">{boardError[a.connectionId!]}</span>}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
          </div>
        </div>

        <div className={row}>
          <div className={rowTitle}>When</div>
          {followsPlan && planned ? (
            <div className="flex flex-col gap-2.5">
              <div className="flex flex-col gap-1 rounded-btn bg-ground px-4 py-3.5">
                <span className="text-[15px] font-semibold text-ink">
                  {planned.type === 'scheduled'
                    ? `Goes live ${dayText(planned.start)} at 9:00 and runs ${planned.days} day${planned.days === 1 ? '' : 's'}, to ${dayText(planned.end)}`
                    : `Goes live when you confirm and runs ${planned.days} day${planned.days === 1 ? '' : 's'}, to ${dayText(planned.end)}`}
                </span>
                <span className="text-small text-ink-2">As set in your monthly plan. Confirming schedules it for that date.</span>
              </div>
              {!readonly && (
                <button type="button" onClick={() => disarmAnd(() => onEdit('launch.timing.source', 'owner', 'Picking another launch date'))}
                  className="self-start border-0 bg-transparent p-0 text-small font-semibold text-ink-2 underline decoration-line-2 underline-offset-4 hover:text-ink">
                  Pick another date
                </button>
              )}
            </div>
          ) : (
          <div className="flex flex-col gap-2.5">
            {planned && ownDate && !isLive && !readonly && (
              <button type="button" onClick={() => { planApplied.current = false; disarmAnd(() => onEdit('launch.timing.source', 'plan', 'Back to the plan’s launch date')); }}
                className="self-start border-0 bg-transparent p-0 text-small font-semibold text-brand-ink underline underline-offset-4">
                Use the plan’s date ({dayText(planned.start)})
              </button>
            )}
            <div className="grid grid-cols-2 gap-2">
              {(['now', 'scheduled'] as const).map((t) => (
                <label key={t} className={cn('flex min-h-[52px] items-center gap-2.5 rounded-btn px-4 font-semibold',
                  timingType === t ? 'shadow-[inset_0_0_0_2.5px_rgb(var(--c-ink-1))]' : 'shadow-ring-line', !readonly && 'cursor-pointer')}>
                  <input type="radio" name="launch-when" disabled={readonly} checked={timingType === t} onChange={() => setTiming(t)} className="h-[18px] w-[18px] accent-[rgb(var(--c-ink-1))]" />
                  {t === 'now' ? 'Now' : 'Scheduled'}
                </label>
              ))}
            </div>
            {timingType === 'scheduled' && (
              <div className="flex flex-col gap-1.5">
                <span className="text-small text-ink-2">Starts</span>
                {/* Launches go out on the hour or half hour (lib/publishSlots). */}
                <div className="grid grid-cols-[1fr_auto] gap-2">
                  <input type="date" aria-label="Start day" disabled={readonly} value={scheduledAt?.slice(0, 10) ?? ''}
                    onChange={(e) => e.target.value && disarmAnd(() => onEdit('launch.timing.at', `${e.target.value}T${toSlot(scheduledAt?.slice(11, 16) || '09:00')}`, 'Scheduled launch time updated'))}
                    className="min-h-[52px] min-w-0 rounded-btn border-0 bg-ground px-4 text-[15px] font-semibold text-ink" />
                  <select aria-label="Start time" disabled={readonly || !scheduledAt} value={toSlot(scheduledAt?.slice(11, 16) || '09:00')}
                    onChange={(e) => scheduledAt && disarmAnd(() => onEdit('launch.timing.at', `${scheduledAt.slice(0, 10)}T${e.target.value}`, 'Scheduled launch time updated'))}
                    className="min-h-[52px] rounded-btn border-0 bg-ground px-4 pr-8 text-[15px] font-semibold text-ink">
                    {TIME_SLOTS.map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
              </div>
            )}
            {days ? <span className="text-small text-ink-2">Runs for {days} days.</span> : null}
          </div>
          )}
        </div>

        {mode === 'paid' && (hasCriticalBudget || healthLines.length > 0) && (
          <div className={row}>
            <div className={rowTitle}>Budget check</div>
            <div className="flex flex-col gap-2">
              {healthLines.map(([p, h]) => (
                <div key={p} className="flex gap-2.5 text-sm leading-snug">
                  <span className={cn('font-bold', h.status === 'critical' ? 'text-bad' : h.status === 'ok' || h.status === 'healthy' ? 'text-ok' : 'text-warn')}>
                    {h.status === 'ok' || h.status === 'healthy' ? '✓' : '!'}
                  </span>
                  <span><b className="capitalize">{p}</b> · {h.implications}</span>
                </div>
              ))}
              {hasCriticalBudget && <p className="m-0 text-small font-semibold text-bad">Too low to launch on {criticalPlatforms.join(', ')}. Increase the budget, or open Strategy to review it.</p>}
            </div>
          </div>
        )}

        {mode === 'paid' && trackingLines.length > 0 && (
          <div className={row}>
            <div className={rowTitle}>Tracking check</div>
            <div className="flex flex-col gap-2">
              {trackingLines.flatMap(([p, t]) => t.checks.map(c => (
                <div key={`${p}-${c.key}`} className="flex gap-2.5 text-sm leading-snug">
                  <span className={cn('font-bold', c.ok ? 'text-ok' : c.severity === 'block' ? 'text-bad' : 'text-warn')}>{c.ok ? '✓' : '!'}</span>
                  <span><b className="capitalize">{p}</b> · {c.text}</span>
                </div>
              )))}
              {trackingBlocked && (
                <label className="flex items-start gap-2 text-small font-semibold text-bad">
                  <input type="checkbox" disabled={readonly} checked={ackTracking} onChange={(e) => setAckTracking(e.target.checked)} className="mt-0.5 h-4 w-4" />
                  Results won’t be counted until this is fixed, so the platform can’t optimise. Launch anyway.
                </label>
              )}
            </div>
          </div>
        )}
      </section>

      {/* Decide panel */}
      <aside aria-label="Confirm spend" className="flex flex-[1_1_320px] flex-col gap-[18px] rounded-panel bg-navy p-7 text-on-navy md:max-w-[400px]">
        <div className="text-label uppercase text-on-navy-3">{mode === 'paid' ? (armed ? 'Confirm the spend' : 'You’re about to spend') : 'Organic posts'}</div>
        <div className="font-money text-[44px] font-semibold leading-none tracking-[-0.02em]">{mode === 'paid' ? amount : 'Free'}</div>
        <div className="text-sm leading-normal text-on-navy-2">
          {perDay ? `≈ ${currency}${Math.round(perDay).toLocaleString('en-IN')} a day · ` : ''}starts {whenText}
          {mode === 'paid' ? ' · the ad platform charges your card on file, not Admeru.' : '.'}
        </div>
        <dl className="m-0 flex flex-col gap-2 border-y border-navy-3 py-3.5 text-sm">
          <div className="flex justify-between gap-3"><dt className="text-on-navy-3">{mode === 'paid' ? 'Ads' : 'Posts'}</dt><dd className="m-0 text-right">{mode === 'paid' ? `${metaCount} Meta · ${googleCount} Google` : `${selectedPosts.length} selected`}</dd></div>
          <div className="flex justify-between gap-3"><dt className="text-on-navy-3">Accounts</dt><dd className="m-0 text-right">{enabledAccounts.length || '—'}</dd></div>
          {state.audience?.summary && <div className="flex justify-between gap-3"><dt className="text-on-navy-3">Who</dt><dd className="m-0 max-w-[60%] truncate text-right" title={state.audience.summary}>{state.audience.summary}</dd></div>}
        </dl>
        {!readonly && (
          <div className="flex flex-col gap-2 rounded-2xl bg-navy-2 p-3.5">
            <div className="text-small font-semibold">{armed ? 'Step 2 of 2 · confirm' : 'Step 1 of 2 · check'}</div>
            <button type="button" onClick={handleLaunch} disabled={confirmDisabled}
              className="inline-flex min-h-[56px] items-center justify-center rounded-btn bg-brand px-5 font-display text-base font-bold text-brand-on hover:bg-brand-hover disabled:cursor-not-allowed disabled:opacity-50">
              {isLaunching || approving ? 'Launching…'
                : armed ? (mode === 'paid' ? `Confirm — spend ${amount}` : 'Confirm — post now')
                : whenType === 'now' ? 'Review spend' : 'Review and schedule'}
            </button>
            {armed && <button type="button" onClick={() => setArmed(false)} className="min-h-[40px] rounded-btn text-sm font-semibold text-on-navy-2 hover:text-on-navy">Back</button>}
            <div className="text-xs text-on-navy-3">
              {enabledAccounts.length === 0 ? 'Select at least one connected account to launch.'
                : missingSubTargets.length > 0 ? `Pick a ${needsBoard(missingSubTargets[0]) ? 'Pinterest board' : 'Facebook Page'} for ${missingSubTargets[0].accountName} to continue.`
                : armed ? 'This is the real thing — the second click commits.' : 'Nothing is spent until you confirm.'}
            </div>
          </div>
        )}
        {launchError && (
          <div role="alert" className="flex flex-col gap-1 text-small leading-snug text-on-navy-bad">
            <span className="font-semibold">! The campaign was not launched.</span>
            <span className="text-on-navy-2">{launchError}</span>
          </div>
        )}
        <div className="text-xs text-on-navy-3">Only owners and admins can confirm spend.{isDemo ? ' Demo campaigns create no real ads.' : ''}</div>
      </aside>
    </div>
  );
};
